import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';

// The CSW caches the handlers from its first render: its zustand store is created there and never
// refreshed, because useStoreSync compares tab configs with JSON.stringify, which cannot see functions.
// On a slow first load that render is one React never committed, so a call from the widget reached a
// handler, or a state setter, of a fiber that is not in the tree - React dropped every update and the
// placeholders stayed up for ever. The mounted component publishes its handler here after each commit,
// and the copy the widget cached delegates to it.
export type LiveHandler<Handler extends (...args: never[]) => void> = {
  // Called from an effect of the mounted component, so a render React never committed cannot publish.
  publish: (handler: Handler) => void;
  // Called from the cleanup of that effect. It clears only the handler it is given, so the cleanup
  // of an instance that unmounts cannot drop the handler a newly mounted instance published.
  unpublish: (handler: Handler) => void;
  // Hand this one to the widget: it always reaches the handler of the mounted instance.
  live: Handler;
};

// A state setter is often called with an updater function, which JSON.stringify turns into null, and
// the helper is generic, so an argument can also be something JSON cannot serialise. The log must
// stay informative for the first case and must not throw for the second.
const describeArgs = (args: unknown[]): string =>
  args
    .map((arg) => {
      if (typeof arg === 'function') return 'function';
      try {
        return JSON.stringify(arg);
      } catch {
        return `[unserializable ${typeof arg}]`;
      }
    })
    .join(', ');

export const createLiveHandler = <Handler extends (...args: never[]) => void>(name: string): LiveHandler<Handler> => {
  let liveHandler: Handler | undefined;

  const live = ((...args: Parameters<Handler>) => {
    if (!liveHandler) {
      clientAppLog({
        level: 'WARN',
        codeSourceName: 'liveHandler',
        logMessage: `${name} called before a mounted handler was published, call dropped. args:${describeArgs(args)}`,
        url: window?.location?.href,
      });
      return;
    }
    liveHandler(...args);
  }) as Handler;

  return {
    publish: (handler) => {
      liveHandler = handler;
    },
    unpublish: (handler) => {
      if (liveHandler === handler) liveHandler = undefined;
    },
    live,
  };
};
